import { useState } from 'react';
import { PasswordInput, Progress, Popover } from '@mantine/core';

import PasswordRequirement from './PasswordRequirement';

import {
  NUMBER_REGEX,
  LOWERCASE_REGEX,
  UPPERCASE_REGEX,
  SYMBOL_REGEX,
} from '../../constants/customPasswordInput';

import { CustomPasswordInputProps } from '../../@types/global';

type GetStrengthArgs = {
  password: string;
  rules: {
    minLength: number;
    needsLowerCase: boolean;
    needsUpperCase: boolean;
    needsNumber: boolean;
    needsSymbol: boolean;
  };
};

function getStrength({ password, rules }: GetStrengthArgs) {
  const activeRules = Object.entries(rules).filter(([key, value]) => {
    if (key === 'minLength') {
      return false;
    }
    return value;
  });

  let multiplier = activeRules.length + 1;

  if (password.length >= rules.minLength) {
    multiplier -= 1;
  }

  if (rules.needsNumber) {
    multiplier = NUMBER_REGEX.test(password) ? multiplier - 1 : multiplier;
  }

  if (rules.needsLowerCase) {
    multiplier = LOWERCASE_REGEX.test(password) ? multiplier - 1 : multiplier;
  }

  if (rules.needsUpperCase) {
    multiplier = UPPERCASE_REGEX.test(password) ? multiplier - 1 : multiplier;
  }

  if (rules.needsSymbol) {
    multiplier = SYMBOL_REGEX.test(password) ? multiplier - 1 : multiplier;
  }

  return Math.max(100 - 20 * multiplier, 10);
}

export default function WithStrengthMeter({
  value = '',
  minLength = 8,
  needsLowerCase = false,
  needsUpperCase = false,
  needsNumber = false,
  needsSymbol = false,
  popoverProps = {},
  ...rest
}: CustomPasswordInputProps) {
  const [popoverOpened, setPopoverOpened] = useState(false);

  const strength = getStrength({
    password: value,
    rules: {
      minLength,
      needsLowerCase,
      needsUpperCase,
      needsNumber,
      needsSymbol,
    },
  });

  let color = 'red';

  if (strength === 100) {
    color = 'green';
  } else if (strength > 50) {
    color = 'yellow';
  }

  return (
    <Popover {...popoverProps} opened={popoverOpened} shadow="lg">
      <Popover.Target>
        <div
          onFocusCapture={() => setPopoverOpened(true)}
          onBlurCapture={() => setPopoverOpened(false)}
        >
          <PasswordInput {...rest} />
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Progress color={color} value={strength} size={5} mb="xs" />
        <PasswordRequirement
          label={`Includes at least ${minLength} characters`}
          passes={value?.length >= minLength}
        />
        {needsNumber && (
          <PasswordRequirement
            label="Includes at least one number"
            passes={NUMBER_REGEX.test(value)}
          />
        )}
        {needsLowerCase && (
          <PasswordRequirement
            label="Includes at least one lowercase letter"
            passes={LOWERCASE_REGEX.test(value)}
          />
        )}
        {needsUpperCase && (
          <PasswordRequirement
            label="Includes at least one uppercase letter"
            passes={UPPERCASE_REGEX.test(value)}
          />
        )}
        {needsSymbol && (
          <PasswordRequirement
            label="Includes at least one symbol"
            passes={SYMBOL_REGEX.test(value)}
          />
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
